import { useReportTemplateFileUpload } from '@consigli/hooks';
import { FC, ReactNode, useState } from 'react';

import { Field, SheetForm } from '@/components';
import { FileUpload } from '@/components/file-upload';
import { errorToast, successToast } from '@/components/toast';

import { TemplateMetadataFields } from './components';

interface UploadTemplateFormProps {
  trigger?: ReactNode;
}

export const UploadTemplateForm: FC<UploadTemplateFormProps> = ({ trigger }) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState('');
  const [projectType, setProjectType] = useState('');
  const [location, setLocation] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { upload } = useReportTemplateFileUpload();

  const handleSubmit = async (): Promise<boolean> => {
    if (isUploading) {
      console.log('Form submission already in progress');
      return false;
    }

    // Validation
    if (!file) {
      setError('Please select a file to upload');
      return false;
    }

    if (!name || !reportType || !projectType || !location) {
      setError('Please fill in all required fields');
      return false;
    }

    // Validate file extension - only .md files allowed
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'md') {
      setError('Only Markdown (.md) files are allowed');
      return false;
    }

    setIsUploading(true);
    setError(undefined);

    try {
      await upload(file, {
        name,
        report_type: reportType,
        project_type: projectType,
        location,
      });

      successToast({ message: 'Report template uploaded successfully' });

      // Reset form
      setFile(null);
      setName('');
      setReportType('');
      setProjectType('');
      setLocation('');

      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create template';
      setError(errorMessage);
      errorToast({ message: errorMessage });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setName('');
    setReportType('');
    setProjectType('');
    setLocation('');
    setError(undefined);
  };

  return (
    <SheetForm
      title="Create Report Template"
      description="Create a new report template record with file and metadata"
      trigger={trigger}
      submitLabel={isUploading ? 'Creating...' : 'Create Template'}
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSubmitting={isUploading}
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <Field
          htmlFor="template-file"
          label="Template File"
          required
          error={!file && error ? 'File is required' : undefined}
        >
          <FileUpload
            files={file ? [file] : []}
            setFiles={(files: File[] | ((prev: File[]) => File[])) => {
              const newFiles = typeof files === 'function' ? files([]) : files;
              setFile(newFiles[0] || null);
            }}
            disabled={isUploading}
            acceptedFileFormats={['.md']}
          />
          <p className="mt-1 text-sm text-gray-500">
            Accepted format: Markdown (.md) - Plain text templates with formatting support
          </p>
        </Field>

        <TemplateMetadataFields
          name={name}
          reportType={reportType}
          projectType={projectType}
          location={location}
          onNameChange={setName}
          onReportTypeChange={setReportType}
          onProjectTypeChange={setProjectType}
          onLocationChange={setLocation}
          disabled={isUploading}
          showError={!!error}
        />
      </div>
    </SheetForm>
  );
};
